/**
 * Early-morning booking confirm gate.
 *
 * One mechanism for every submit path in the booking flow: `intercept` pauses
 * the caller's handler and opens the confirm prompt; on "Yes, Continue" the
 * caller's own handler is RE-ENTERED so every other refusal — waiver, price,
 * single-submit — is re-evaluated at confirm time rather than skipped.
 *
 * A confirmed start time stays confirmed: a submit that then fails (a form
 * validation error, a refused booking) and is retried does not ask again.
 * Picking a different time asks afresh.
 *
 * @module pages/booking/shared
 */

import { useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { isEarlyMorningSlotTime } from '@butter/library/datetime';

export function useEarlyBookingConfirm(startTime: string | null) {
  const [open, setOpen] = useState(false);
  // Refs, not state: `resume` re-enters the caller's handler synchronously in
  // the same tick, where a state write would not be visible yet (the prompt
  // would reopen).
  const resumeRef = useRef<(() => void) | null>(null);
  const confirmedStartRef = useRef<string | null>(null);

  /**
   * Call synchronously in the handler, before its first `await`. Returns true
   * when the caller must stop — the prompt is open and `resume` runs on "Yes".
   * `resume` must itself re-enter synchronously (a call, `button.click()`, or
   * `form.requestSubmit()` all dispatch synchronously).
   */
  const intercept = (resume: () => void): boolean => {
    if (!startTime || !isEarlyMorningSlotTime(startTime)) return false;
    if (confirmedStartRef.current === startTime) return false;
    resumeRef.current = resume;
    setOpen(true);
    return true;
  };

  /**
   * `onClick` of a submit button, including one tied to a form elsewhere by
   * `form=`. Returns true when the click was held: its default (the form submit)
   * is prevented, and "Yes" clicks the same button again.
   */
  const interceptClick = (event: MouseEvent<HTMLButtonElement>): boolean => {
    const button = event.currentTarget;
    if (!intercept(() => button.click())) return false;
    event.preventDefault();
    return true;
  };

  /**
   * `onSubmitCapture` on an ancestor of a form whose own `onSubmit` does
   * something irreversible. Capture runs before the form's handler, so the
   * submit is held before it starts; "Yes" re-submits the same form.
   */
  const captureSubmit = (event: FormEvent<HTMLElement>) => {
    const form = event.target as HTMLFormElement;
    if (intercept(() => form.requestSubmit())) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const confirm = () => {
    const resume = resumeRef.current;
    resumeRef.current = null; // a second "Yes" is a no-op → exactly once
    setOpen(false);
    if (!resume) return;
    confirmedStartRef.current = startTime;
    resume();
  };

  const cancel = () => {
    resumeRef.current = null;
    setOpen(false);
  };

  return { intercept, interceptClick, captureSubmit, open, confirm, cancel };
}
